import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Globe, MapPin, LineChart, Radar, Layers, Database, Satellite } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { GIS_PAGE } from "@/constants/testIds";

const capabilities = [
  { icon: MapPin, title: "Live Mapping", desc: "Interactive Mapbox / Leaflet maps with clustering, heatmaps, and vector tiles tuned for millions of features." },
  { icon: Radar, title: "Field Tracking", desc: "Offline-capable mobile field apps with GPS logging, geofence alerts, and background sync." },
  { icon: Layers, title: "Spatial Layers", desc: "PostGIS-powered spatial queries, layer management, and time-series comparisons for change detection." },
  { icon: LineChart, title: "Analytics", desc: "Dashboards that turn coordinates into decisions — density, coverage, movement, and impact metrics." },
  { icon: Database, title: "Data Pipelines", desc: "ETL from shapefiles, GeoJSON, KML, CSV, or IoT streams into a single validated spatial store." },
  { icon: Satellite, title: "Remote Sensing", desc: "Satellite imagery ingestion, NDVI / water-detection processing, and time-lapse visualizations." },
];

const useCases = [
  { tag: "Environmental", title: "River & marine cleanup surveys", desc: "Field teams collect debris data with GPS + photos; managers see live coverage maps and monthly impact." },
  { tag: "Government", title: "Population & asset registries", desc: "Citizen locations, service coverage, and infrastructure inventories mapped at national scale." },
  { tag: "Enterprise", title: "Logistics & operations", desc: "Route optimization, fleet visibility, and geofenced workflow triggers for large field workforces." },
  { tag: "Tourism", title: "Destination discovery", desc: "Map-first exploration with points-of-interest layers, trails, and multi-language descriptions." },
];

export default function GisPage() {
  return (
    <div id="gis-top" data-testid={GIS_PAGE.root}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 hud-grid opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-70 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 30%, rgba(34,211,238,0.14), transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(217,70,239,0.10), transparent 55%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Link
            to="/#services"
            data-testid={GIS_PAGE.back}
            className="inline-flex items-center gap-2 font-hud text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft size={12} /> back_to_services
          </Link>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="overline">/ 08 gis.systems</div>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-5 font-mono-display text-4xl sm:text-5xl md:text-6xl font-extrabold uppercase tracking-tighter leading-[0.95] text-slate-100"
              >
                <span className="block">Location-aware</span>
                <span className="block">
                  <span className="text-neon-cyan">software</span>, engineered
                </span>
                <span className="block">for <span className="text-neon-fuchsia">real terrain.</span></span>
              </motion.h1>
              <p className="mt-6 max-w-2xl text-slate-300 font-body text-base sm:text-lg leading-relaxed">
                We build production-grade Geographic Information Systems for governments, environmental
                partners, logistics operators, and tourism platforms — from mobile field-data capture
                to enterprise spatial analytics.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid={GIS_PAGE.ctaContact}
                  className="btn-neon"
                >
                  Start_a_GIS_build <ArrowRight size={14} />
                </Link>
                <Link
                  to="/#portfolio"
                  data-testid={GIS_PAGE.ctaPortfolio}
                  className="btn-ghost"
                >
                  View_completed_projects
                </Link>
              </div>
            </div>

            {/* Decorative orbiting globe */}
            <div className="lg:col-span-4 flex items-center justify-center">
              <div className="relative w-64 h-64 sm:w-72 sm:h-72">
                <div className="absolute inset-0 rounded-full border border-cyan-500/25 animate-[spin_24s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border border-fuchsia-500/20 animate-[spin_18s_linear_infinite_reverse]" />
                <div className="absolute inset-10 rounded-full border border-cyan-500/40" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Globe className="w-24 h-24 text-cyan-400 animate-[spin_22s_linear_infinite]" strokeWidth={1.2} />
                </div>
                {[0, 60, 120, 180, 240, 300].map((deg) => (
                  <span
                    key={deg}
                    className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-cyan-400 shadow-neon-cyan"
                    style={{ transform: `rotate(${deg}deg) translate(120px) rotate(-${deg}deg)` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="relative py-20 md:py-24 border-t border-cyan-900/40">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="overline">/ capabilities.matrix</div>
              <h2 className="mt-4 font-mono-display text-2xl md:text-4xl font-extrabold uppercase tracking-tighter text-slate-100">
                What our <span className="text-neon-cyan">GIS stack</span> ships with
              </h2>
            </div>
            <p className="max-w-md text-slate-400 font-body">
              Six pillars we compose per engagement — from data ingest to executive dashboards.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
                  className="p-6 bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/40 hover:-translate-y-1 transition-[border-color,transform] duration-300 hud-corners"
                >
                  <span className="hud-c-tl" />
                  <span className="hud-c-br" />
                  <span className="w-11 h-11 flex items-center justify-center border border-cyan-500/40 text-cyan-400">
                    <Icon size={18} />
                  </span>
                  <h3 className="mt-5 font-mono-display uppercase tracking-tight text-slate-100">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-slate-400 text-sm font-body leading-relaxed">
                    {c.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="relative py-20 md:py-24 border-t border-cyan-900/40">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="mb-12">
            <div className="overline">/ use_cases.log</div>
            <h2 className="mt-4 font-mono-display text-2xl md:text-4xl font-extrabold uppercase tracking-tighter text-slate-100">
              Where our maps <span className="text-neon-fuchsia">go to work</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {useCases.map((u, i) => (
              <motion.div
                key={u.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 2) * 0.05 }}
                className="p-6 md:p-7 bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/40 transition-colors duration-300"
              >
                <span className="tag-pill">{u.tag}</span>
                <h3 className="mt-4 font-mono-display text-lg md:text-xl uppercase tracking-tight text-slate-100">
                  {u.title}
                </h3>
                <p className="mt-3 text-slate-400 font-body">{u.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 p-6 md:p-8 border border-cyan-500/30 bg-[#0a0a0a] flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="font-hud text-cyan-400/80">/ featured_case</div>
              <div className="mt-2 font-mono-display text-xl md:text-2xl text-slate-100 uppercase tracking-tight">
                UNDP · River Cleanup Survey
              </div>
              <p className="mt-2 text-slate-400 font-body">
                See how we combined GIS, mobile field capture, and real-time dashboards for UNDP field teams.
              </p>
            </div>
            <Link to="/#portfolio" className="btn-ghost self-start md:self-auto">
              Open_portfolio <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
